<template>
  <div v-if="showSiteFooter" class="site-footer">
    <div class="site-footer-content">
      <div class="top">
        <img class="logo" :src="LogoSrc" alt="TourHero Logo" />
        <div>Bringing us closer as humans, one journey at a time.</div>
        <ContactInfo v-if="mobile" />
      </div>

      <div class="hr"></div>

      <div class="middle">
        <ContactInfo v-if="desktop" />

        <div class="links">
          <div class="heading" @click="toggleAccordion('getStarted')">
            Get started
            <template v-if="mobile">
              <i v-if="accordionState.getStarted" class="icon-arrow-up-1"></i>
              <i v-else class="icon-arrow-down-1"></i>
            </template>
          </div>
          <template v-if="desktop || accordionState.getStarted">
            <NavLink href="/plan-a-trip">Plan a trip</NavLink>
            <NavLink href="/explore">Join a trip</NavLink>
            <NavLink href="/plan-a-trip/explore-destinations">Explore destinations</NavLink>
            <NavLink href="/how-tourhero-works">How TourHero works</NavLink>
          </template>
        </div>

        <div class="links">
          <div class="heading" @click="toggleAccordion('ourSignatureTrips')">
            Our Signature Trips
            <template v-if="mobile">
              <i v-if="accordionState.ourSignatureTrips" class="icon-arrow-up-1"></i>
              <i v-else class="icon-arrow-down-1"></i>
            </template>
          </div>
          <template v-if="desktop || accordionState.ourSignatureTrips">
            <NavLink href="/content-creator-group-travel">Content creator</NavLink>
            <NavLink href="/christian-pilgrimage-bible-study-tours">Christian pilgrimage & Bible study</NavLink>
            <NavLink href="/friends-family-vacation-group-travel">Friends & family</NavLink>
            <NavLink href="/group-hiking-tours">Group hiking tours</NavLink>
            <NavLink href="/heritage-cultural-travel">Heritage & culture</NavLink>
            <NavLink href="/mba-exclusive">MBA exclusive</NavLink>
            <NavLink href="/wellness-retreat">Wellness retreats</NavLink>
            <NavLink href="/women-travel-together-group-tours">Women only</NavLink>
          </template>
        </div>

        <div class="links">
          <div class="heading" @click="toggleAccordion('about')">
            About
            <template v-if="mobile">
              <i v-if="accordionState.about" class="icon-arrow-up-1"></i>
              <i v-else class="icon-arrow-down-1"></i>
            </template>
          </div>
          <template v-if="desktop || accordionState.about">
            <NavLink href="/about">About us</NavLink>
            <NavLink href="/en/magazine/">TourHero Magazine</NavLink>
            <NavLink href="/team/">TourHero team</NavLink>
            <NavLink
              v-if="isFeatureEnabled('careers')"
              href="https://careers.tourhero.com/"
              target="_blank"
              rel="noreferrer noopener"
              >Careers</NavLink
            >
            <NavLink href="/en/magazine/life-at-tourhero/">Life at TourHero</NavLink>
            <NavLink href="https://www.tourhero.com/reviews/" target="_blank" rel="noreferrer noopener"
              >TourHero reviews
            </NavLink>
            <NavLink href="/about#contact_us">Contact us</NavLink>
            <NavLink
              exact-url
              href="https://www.google.com/preferences/source?q=tourhero.com"
              target="_blank"
              rel="noreferrer noopener"
              >Prefer TourHero on Google
            </NavLink>
          </template>
        </div>

        <div class="links">
          <div class="heading" @click="toggleAccordion('legal')">
            Legal
            <template v-if="mobile">
              <i v-if="accordionState.legal" class="icon-arrow-up-1"></i>
              <i v-else class="icon-arrow-down-1"></i>
            </template>
          </div>
          <template v-if="desktop || accordionState.legal">
            <NavLink href="/terms">Terms</NavLink>
            <NavLink href="/privacy-policy">Privacy policy</NavLink>
          </template>
        </div>
      </div>

      <div class="hr"></div>

      <div class="bottom">
        <div class="disclaimer">© {{ new Date().getFullYear() }} TourHero Inc. All rights reserved</div>
        <Socials />
      </div>
    </div>
  </div>
</template>

<script setup>
import LogoSrc from 'app/assets/images/logo.png'
import Socials from '@/modules/SiteFooter/Socials.vue'
import { useShared } from '@/helpers/vueHelpers.js'
import ContactInfo from '@/modules/SiteFooter/ContactInfo.vue'
import NavLink from '@/components/NavLink.vue'
import { useGlobalState } from '@/stores/global.js'
import { ref } from 'vue'
import { isFeatureEnabled } from '@/utils/featureUtils.js'

const { mobile, desktop } = useShared()
const { showSiteFooter } = useGlobalState()

const accordionState = ref({
  getStarted: false,
  ourSignatureTrips: true,
  about: false,
  legal: false,
})

const toggleAccordion = key => {
  if (!(key in accordionState.value)) {
    return
  }

  accordionState.value[key] = !accordionState.value[key]
}
</script>

<style lang="scss" scoped>
.site-footer {
  background-color: #e3f2fd;
  border-radius: 24px 24px 0 0;
}

.site-footer-content {
  @include main-container;
  @include main-wrapper;

  font-size: $text-base;
  line-height: $leading-snug;
  font-weight: 500;
  color: $brand-primary;
  padding-block: 32px;

  @include mobile {
    padding-block: 24px;
  }

  .hr {
    opacity: 0.5;
    margin: 32px 0;
    border-bottom: 1px solid $blue-75;
  }

  .top {
    display: grid;
    gap: 24px;

    .logo {
      height: 32px;
    }
  }

  .middle {
    display: grid;
    grid-template-columns: 0.7fr auto auto auto auto;
    row-gap: 32px;
    column-gap: 50px;
    align-items: start;

    @include mobile {
      grid-template-columns: 1fr;
    }

    .links {
      @include flex-column(16px);

      .heading {
        font-size: 12px;
        line-height: 14px;
        font-weight: 600;
        text-transform: uppercase;
        margin-bottom: 8px;

        @include mobile {
          margin-bottom: 4px;

          i:first-of-type {
            margin-left: $spacing-2;
          }
        }
      }

      a {
        color: $brand-primary;
      }
    }
  }

  .bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;

    @include mobile {
      align-items: flex-start;
      flex-direction: column-reverse;
    }
  }
}
</style>
